import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, FormControlLabel, Grid, InputAdornment, Stack, Tooltip, Typography } from '@mui/material';
import distance from '@turf/distance';
import { isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useDevice } from '@/api/devices';
import { usePostActivateGeofence, usePostDeactivateGeofence, usePostUpdateGeofence } from '@/api/geofence';
import { UserDisplayPreferencesExtended } from '@/api/userAccount';
import { convert } from '@/common/convert';
import { TetherInfoAlert } from '@/components/TetherInfoAlert';
import { Datum } from '@/components/data/Datum';
import { SensorIcon } from '@/components/data/SensorIcon';
import { FormInputText } from '@/components/form/FormInputText';
import { SpotterGeofenceMap } from '@/components/map/SpotterGeofenceMap';
import { GeofenceLayerProps } from '@/components/map/layers/GeofenceLayer';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Switch } from '@/components/ui/Switch';
import { ResetUpdateButtonGroup } from '@/components/userAccount/ResetUpdateButtonGroup';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePreferredUnits } from '@/hooks/usePreferredUnits';
import { useTetherDeviceIdentity } from '@/hooks/useTetherDeviceIdentity';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';

type GeofenceFormValues = {
    latitude: string;
    longitude: string;
    radius: string;
    units: UserDisplayPreferencesExtended['length'];
};

const geofenceDefaultValues: GeofenceFormValues = { latitude: '', longitude: '', radius: '', units: 'm' };

export const MAX_GEOFENCE_RADIUS_METERS = 1000;
const MAX_GEOFENCE_RADIUS_FT = Math.floor(convert(MAX_GEOFENCE_RADIUS_METERS).from('m').to('ft'));

const ScoutGeofenceSection = ({ spotterGeofenceAllowed }: { spotterGeofenceAllowed: boolean }) => {
    const { tetherDashboardUrl } = useTetherDeviceIdentity();

    if (spotterGeofenceAllowed) {
        return (
            <TetherInfoAlert
                title="Use Tether geofence to follow Scout missions"
                href={tetherDashboardUrl}
                actionButtonLabel="Configure on Tether"
            >
                <Stack gap={0.5}>
                    <Box>
                        Geofence notifications configured on Tether automatically follow the Scout&apos;s expected
                        location throughout its mission.
                    </Box>
                    <Box>
                        An additional static geofence tracked and reported through Spotter can be configured below. It
                        must be disabled manually to avoid false alarms during Spotter Scout relocations.
                    </Box>
                </Stack>
            </TetherInfoAlert>
        );
    }
    return (
        <Stack gap={2}>
            <Stack
                direction="row"
                sx={{ ml: 0, width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 5 }}
            >
                <Typography>Geofence notifications</Typography>
                <Button
                    href={tetherDashboardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="medium"
                    variant="contained"
                >
                    Configure on Tether <OpenInNewIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                </Button>
            </Stack>
        </Stack>
    );
};

const FullGeofenceSection = ({ readOnly }: { readOnly?: boolean }) => {
    const { mutateAsync: activateDataGeofence } = usePostActivateGeofence();
    const { mutateAsync: deactivateDataGeofence } = usePostDeactivateGeofence();
    const { mutateAsync: updateDataGeofence } = usePostUpdateGeofence();
    const { data: device } = useDevice();
    const spotterId = device?.spotterId;
    const units = usePreferredUnits('length');
    const [centerMapTrigger, setCenterMapTrigger] = useState<number>(0);

    const {
        control: geofenceFormControl,
        reset: geofenceFormReset,
        setValue: setGeofenceFormValue,
        watch: watchGeofenceForm,
        handleSubmit: changeGeofenceHandleSubmit,
        formState: geofenceFormState,
    } = useForm<GeofenceFormValues>({
        defaultValues: geofenceDefaultValues,
        mode: 'onChange',
    });
    const [isGeofenceSwitchOn, setIsGeofenceSwitchOn] = useState(device?.waves.geofenceEnabled ?? false);

    /*
     * When the geofence has never been set, the form is auto-centered on the Spotter's current location on mount
     * (see the effect below) to support one-click enabling. That programmatic pre-fill marks the form dirty even
     * though the user hasn't edited anything, so we record the auto-applied values here and only treat the form as
     * having unsaved changes once the user moves away from that baseline.
     */
    const autoCenteredBaselineRef = useRef<GeofenceFormValues | null>(null);

    const currentLocation = device?.track?.[0];
    const [formLatitude, formLongitude, formRadius] = watchGeofenceForm(['latitude', 'longitude', 'radius']);

    const hasGeofenceLatLonRadiusSet =
        !isNil(device?.waves.geofenceLat) && !isNil(device?.waves.geofenceLon) && !isNil(device?.waves.geofenceRadius);

    const autoCenteredBaseline = autoCenteredBaselineRef.current;
    // The auto-centered baseline only suppresses the guard before the geofence has ever been saved. Once it's set,
    // any value matching the (now stale) baseline is a real user edit, so we stop honoring the baseline entirely.
    const matchesAutoCenteredBaseline =
        !hasGeofenceLatLonRadiusSet &&
        autoCenteredBaseline !== null &&
        formLatitude === autoCenteredBaseline.latitude &&
        formLongitude === autoCenteredBaseline.longitude &&
        formRadius === autoCenteredBaseline.radius;
    useUnsavedChangesGuard(geofenceFormState.isDirty && !matchesAutoCenteredBaseline, 'geofence');

    const currentDistanceKm = useMemo(() => {
        if (!currentLocation || isNil(formLatitude) || isNil(formLongitude)) {
            return null;
        }
        return distance(
            [Number(formLongitude), Number(formLatitude)],
            [currentLocation.longitude, currentLocation.latitude],
            {
                units: 'kilometers',
            }
        );
    }, [currentLocation, formLatitude, formLongitude]);

    const handleSwitchClick = async () => {
        if (!spotterId) {
            // eslint-disable-next-line no-console
            console.log('No Spotter selected');
            return;
        }
        if (isGeofenceSwitchOn) {
            await deactivateDataGeofence({ spotterId });
        } else {
            if (!hasGeofenceLatLonRadiusSet) {
                // if this is the first time it's been set, try to save our form -- the only time we allow a single-click save-and-enable on the toggle
                await onGeofenceSubmit();
                // assuming it's successful, onGeofenceSubmit will handle activating the fence, so nothing else to do here
                return;
            }
            await activateDataGeofence({ spotterId });
        }
    };

    const onGeofenceReset = () => {
        if (!device) {
            return;
        }
        const radius = isNil(device.waves.geofenceRadius)
            ? ''
            : `${Math.round(convert(device.waves.geofenceRadius).from('m').to(units))}`;
        geofenceFormReset({
            radius,
            units,
            latitude: `${device?.waves.geofenceLat}`,
            longitude: `${device?.waves.geofenceLon}`,
        });
    };

    const onGeofenceSubmit = changeGeofenceHandleSubmit(async (formData: GeofenceFormValues) => {
        if (!spotterId) {
            // eslint-disable-next-line no-console
            console.log('GeofenceSection: onGeofenceSubmit: No Spotter selected');
            return;
        }
        try {
            const radius = Math.round(convert(Number(formData.radius)).from(formData.units).to('m'));
            await updateDataGeofence({
                spotterId,
                radius,
                latitude: Number(formData.latitude),
                longitude: Number(formData.longitude),
            });

            if (!isGeofenceSwitchOn) {
                await activateDataGeofence({ spotterId: spotterId });
            }
        } catch (err) {
            console.error(err);
            onGeofenceReset();
        }
    });

    const { radiusMin, radiusMax, radiusDefault } = useMemo(() => {
        switch (units) {
            case 'm':
                return { radiusMin: 1, radiusMax: MAX_GEOFENCE_RADIUS_METERS, radiusDefault: 300 };
            case 'ft':
                return { radiusMin: 3, radiusMax: MAX_GEOFENCE_RADIUS_FT, radiusDefault: 1000 };
        }
    }, [units]);

    const setCenterToCurrentLocation = useCallback((): GeofenceFormValues | null => {
        if (!currentLocation) {
            return null;
        }

        const latitude = String(currentLocation.latitude);
        const longitude = String(currentLocation.longitude);
        const radius = formRadius || String(radiusDefault);

        setGeofenceFormValue('latitude', latitude, { shouldDirty: true });
        setGeofenceFormValue('longitude', longitude, { shouldDirty: true });
        if (!formRadius) {
            setGeofenceFormValue('radius', radius, { shouldDirty: true });
        }
        setCenterMapTrigger(Date.now());

        return { latitude, longitude, radius, units };
    }, [currentLocation, formRadius, radiusDefault, setGeofenceFormValue, units]);

    const handleCenterClick = useCallback(() => {
        // A manual re-center is a genuine user edit, so it clears the auto-centered baseline that the navigation
        // guard treats as "not really dirty" (only the mount-time auto-centering establishes that baseline).
        setCenterToCurrentLocation();
        autoCenteredBaselineRef.current = null;
    }, [setCenterToCurrentLocation]);

    const setFormCenter: GeofenceLayerProps['setCenter'] = ({ lng, lat }) => {
        setGeofenceFormValue('longitude', lng.toFixed(5), { shouldDirty: true });
        setGeofenceFormValue('latitude', lat.toFixed(5), { shouldDirty: true });
    };

    const setFormRadius = (radius: number) => {
        // flooring in case the conversion to feet took us just over the max
        setGeofenceFormValue('radius', Math.floor(radius).toString(), {
            shouldDirty: true,
        });
    };

    useEffect(() => {
        if (!device) {
            return;
        }

        setIsGeofenceSwitchOn(device.waves.geofenceEnabled ?? false);
    }, [device]);

    // decomposing outside the effect so that we don't reset a form edit in progress unless something actually changed
    const { geofenceRadius, geofenceLat, geofenceLon } = device?.waves ?? {};

    useEffect(() => {
        if (!geofenceRadius || !geofenceLat || !geofenceLon) {
            return;
        }
        const radius = isNil(geofenceRadius) ? '' : `${Math.floor(convert(geofenceRadius).from('m').to(units))}`;

        geofenceFormReset({
            radius,
            units,
            latitude: `${geofenceLat}`,
            longitude: `${geofenceLon}`,
        });
    }, [units, geofenceFormReset, geofenceLat, geofenceLon, geofenceRadius]);

    useEffect(() => {
        // if we have the device but fields aren't set, initialize the form to a reasonable default to allow one-click enabling
        if (!device) {
            return;
        }

        if (!hasGeofenceLatLonRadiusSet && !geofenceFormState.isDirty) {
            // Record the auto-applied values as the baseline the navigation guard treats as "not really dirty".
            autoCenteredBaselineRef.current = setCenterToCurrentLocation();
        }
    }, [device, geofenceFormState.isDirty, hasGeofenceLatLonRadiusSet, setCenterToCurrentLocation]);

    const disableToggleDueToDirty = useMemo(() => {
        if (!geofenceFormState.isDirty) {
            return false;
        }

        // if it is dirty, but this is the first time it's ever been set, let them enable it with one click as long as we have a location to initialize it on
        return hasGeofenceLatLonRadiusSet || !currentLocation;
    }, [currentLocation, geofenceFormState.isDirty, hasGeofenceLatLonRadiusSet]);

    return (
        <Stack gap={2}>
            <FormControlLabel
                value="start"
                sx={{
                    ml: 0,
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: 5,
                }}
                control={
                    <Tooltip title={disableToggleDueToDirty ? 'Save or cancel changes before toggling geofence' : ''}>
                        <Box component="span">
                            <Switch
                                onClick={handleSwitchClick}
                                checked={isGeofenceSwitchOn}
                                disabled={disableToggleDueToDirty || geofenceFormState.isSubmitting}
                                readOnly={readOnly}
                            />
                        </Box>
                    </Tooltip>
                }
                label={
                    <Stack>
                        <Typography>Enable geofence notifications</Typography>
                        <Typography variant="minorLabel">
                            Receive an email alert when your Spotter leaves the preset geofence.
                        </Typography>
                    </Stack>
                }
                labelPlacement="start"
            />
            <Typography variant="body1">
                Set coordinates and radius to receive notifications when the Spotter leaves the geofence.
            </Typography>
            <Grid container spacing={2} justifyContent="space-between" size={{ xs: 12 }}>
                <Grid container gap={2} size={{ xs: 6 }}>
                    <Grid size={{ xs: 12 }}>
                        <FormInputText
                            name="latitude"
                            control={geofenceFormControl}
                            label="Latitude"
                            inputFieldProps={{
                                id: 'geofence-latitude',
                                type: 'number',
                                size: 'medium',
                                disabled: readOnly,
                                InputProps: {
                                    endAdornment: <InputAdornment position="end">degrees</InputAdornment>,
                                },
                            }}
                            controllerProps={{
                                rules: {
                                    required: 'Field is required',
                                    max: { value: 90, message: 'Max latitude value is 90 (degrees).' },
                                    min: {
                                        value: -90,
                                        message: 'Min latitude value is -90 (degrees).',
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <FormInputText
                            name="longitude"
                            control={geofenceFormControl}
                            label="Longitude"
                            inputFieldProps={{
                                id: 'geofence-longitude',
                                type: 'number',
                                size: 'medium',
                                disabled: readOnly,
                                InputProps: {
                                    endAdornment: <InputAdornment position="end">degrees</InputAdornment>,
                                },
                            }}
                            controllerProps={{
                                rules: {
                                    required: 'Field is required',
                                    max: { value: 180, message: 'Max longitude value is 180 (degrees).' },
                                    min: {
                                        value: -180,
                                        message: 'Min longitude value is -180 (degrees).',
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <FormInputText
                            name="radius"
                            control={geofenceFormControl}
                            label="Radius"
                            inputFieldProps={{
                                id: 'geofence-radius',
                                type: 'number',
                                size: 'medium',
                                disabled: readOnly,
                                InputProps: {
                                    endAdornment: <InputAdornment position="end">{units}</InputAdornment>,
                                },
                            }}
                            controllerProps={{
                                rules: {
                                    required: 'Field is required',
                                    max: {
                                        value: radiusMax,
                                        message: `Max radius is ${radiusMax} ${units}.`,
                                    },
                                    min: {
                                        value: radiusMin,
                                        message: `Min radius is ${radiusMin} ${units}.`,
                                    },
                                },
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid size={{ xs: 6 }}>
                    <SpotterGeofenceMap
                        radius={convert(Number(formRadius)).from(units).to('m')}
                        setRadius={(radiusMeters) => setFormRadius(convert(radiusMeters).from('m').to(units))}
                        center={{ lng: Number(formLongitude), lat: Number(formLatitude) }}
                        setCenter={setFormCenter}
                        centerMapTrigger={centerMapTrigger}
                        readOnly={readOnly}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} size={{ xs: 12 }} justifyContent="space-between" alignItems="center">
                <Grid size={{ xs: 6 }}>
                    {!isNil(currentDistanceKm) && (
                        <Typography>
                            Distance to Spotter:{' '}
                            <Datum value={currentDistanceKm} sourceUnit="km" type="length" maxDecimalPlaces={1} />
                        </Typography>
                    )}
                </Grid>
                <Grid size={{ xs: 6 }}>
                    {!isNil(currentLocation) && !readOnly && (
                        <Button variant="outlined" onClick={handleCenterClick} startIcon={<GpsFixedIcon />}>
                            Center on current Spotter location
                        </Button>
                    )}
                </Grid>
            </Grid>

            {!readOnly && (
                <ResetUpdateButtonGroup
                    onReset={onGeofenceReset}
                    onSubmit={onGeofenceSubmit}
                    isResetDisabled={
                        !geofenceFormState.isDirty || geofenceFormState.isSubmitting || !hasGeofenceLatLonRadiusSet
                    }
                    isUpdateDisabled={!geofenceFormState.isDirty || geofenceFormState.isSubmitting}
                    updateLabel={isGeofenceSwitchOn ? 'Update' : 'Enable geofence'}
                    isSubmitting={geofenceFormState.isSubmitting}
                    containerStyle={{
                        justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                        alignItems: 'center',
                        my: 0,
                    }}
                />
            )}
        </Stack>
    );
};

export const GeofenceSection = ({ readOnly }: { readOnly?: boolean }) => {
    const { data: device, isFetching: isDevicesFetching } = useDevice();
    const allowScoutGeofenceFlag = useFeatureFlag('allowScoutGeofence');
    const [wasEnabledThisSession, setWasEnabledThisSession] = useState(false);

    useEffect(() => {
        setWasEnabledThisSession(wasEnabledThisSession || !!device?.waves.geofenceEnabled);
    }, [device?.waves.geofenceEnabled, wasEnabledThisSession]);

    const isScout = device?.hullType === 'scout';
    const allowScoutGeofence = allowScoutGeofenceFlag || device?.waves.geofenceEnabled || wasEnabledThisSession;

    return (
        <Stack gap={2}>
            <SectionHeader isLoading={isDevicesFetching} title="Geofence" icon={<SensorIcon Icon={GpsFixedIcon} />} />
            {isScout && <ScoutGeofenceSection spotterGeofenceAllowed={allowScoutGeofence} />}
            {(!isScout || allowScoutGeofence) && <FullGeofenceSection readOnly={readOnly} />}
        </Stack>
    );
};
